import { useMemo, useRef } from 'react';

/**
 * Keeps a polling fetcher's identity stable while its request window advances.
 * Consumers such as useApiQuery can then finish the active request and queue
 * one refresh, while the next invocation always uses the newest request.
 */
export const useLatestRequestFetcher = <TRequest, TResponse>(
  request: TRequest | null,
  execute: (request: TRequest) => Promise<TResponse>,
  identityKey: string
): (() => Promise<TResponse>) | null => {
  const requestRef = useRef(request);
  const executeRef = useRef(execute);
  requestRef.current = request;
  executeRef.current = execute;
  const enabled = request !== null;

  return useMemo(() => {
    if (!enabled) {
      return null;
    }
    return () => {
      const latestRequest = requestRef.current;
      if (latestRequest === null) {
        return Promise.reject(new Error('Latest polling request is unavailable'));
      }
      return executeRef.current(latestRequest);
    };
  }, [enabled, identityKey]);
};
